import { CartAPI } from '../api/cart';
import { getProductDetail } from '../api/quick-view.js';
import { KEYS } from '../constants/keys';
import { addToCart } from '../modules/cart/cart.legacy';
import { handlePreorderAddToCart, handlePreOrderUI } from '../pre-order';
import { handleTooltip } from '../tooltip/index.js';
import { safeParseJSON } from '../utils';
import { buildProductDetailUrlWithVariant, formatCurrency } from '../utils/format.js';
import { isKeyboardActivated } from '../utils/keyboardActivated.js';
import { focusFirstElement, trapFocus } from '../utils/wcag-modal';
import { applySwatchSettings } from './product-swatch.js';

let sliderRef = null;
let initialized = false;
let loading = false;

// Create a global variable to track the last focused button (support WCAG : when close quick view)
window.buttonQuickviewFocus = null;

/**
 * @type { import('./index.d').quickView }
 */
export const quickView = async (context, action, target) => {
  if (loading) return;

  loading = true;
  await initQuickViewSlider();

  const { productId, fromSelectOptionBtn = false } = action;
  const params = {
    shop: context?.defaultParams?.shop || context?.app?.shop?.domain,
    product_id: productId,
    quickview_widget_id: context.app.templateMetadata.quickView,
    locale: context?.app?.generalSettings?.current_locale || 'en',
    money_format: context.app.shop.money_format,
    money_format_with_currency: context.app.shop.money_format_with_currency,
  };

  let html = await getProductDetail(context, params);

  if (fromSelectOptionBtn) {
    // removed quick view image === selection options - popup
    html = html.replace(
      'class="boost-sd__quick-view-product-image"',
      'class="boost-sd__quick-view-product-image" data-disable-image="' +
        fromSelectOptionBtn +
        '" style="display:none"'
    );
  }

  document.body.insertAdjacentHTML('beforeend', html);
  document.body.classList.add('boost-sd__modal-open-quickview');

  // Track the button that opened the modal
  window.buttonQuickviewFocus = target;

  // dispatch event for analytic
  window.dispatchEvent(
    new CustomEvent('boost-sd-open-quick-view', {
      detail: {
        pid: productId,
      },
    })
  );

  handleQuickViewSlider();
  handleQuickViewModal(context);
  handleTooltip(context);

  //Generate newId for customize
  const newId = window.boostWidgetIntegration.generateUuid();
  context.state.latestQuickViewRequest = newId;
};

/**
 * @type { import('./index.d').initQuickViewSlider }
 */
export const initQuickViewSlider = async () => {
  if (initialized) return;

  const jquery = await import('jquery');
  const $appJQuery = jquery.default.noConflict(true);
  window.$appJQuery = $appJQuery;

  await import('slick-carousel');

  initialized = true;
};

/**
 * @type { import('./index.d').handleQuickViewSlider }
 */
const handleQuickViewSlider = () => {
  const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

  const slider = window.$appJQuery('.boost-sd__quick-view .slick-slider').slick({
    prevArrow: false,
    nextArrow: false,
    useTransform: false,
    rtl: isRtl, // Add this property to enable RTL mode
  });

  if (isRtl) {
    window.$appJQuery('.boost-sd__quick-view .slick-slider').css({
      direction: 'rtl',
    });

    window.$appJQuery('.boost-sd__quick-view .slick-slider .slick-slide').css({
      float: 'right',
    });
  }

  sliderRef = slider;

  const currentSlide = sliderRef.slick('slickCurrentSlide');

  selectThumbnail(typeof currentSlide === 'number' ? currentSlide : 0);

  sliderRef.on('beforeChange', (event, slick, currentSlide, nextSlide) => {
    selectThumbnail(nextSlide);
  });

  document
    .querySelectorAll('.boost-sd__thumbs-container-thumbs-item')
    .forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        slider.slick('slickGoTo', index);
      });

      // Support WCAG : selected thumbnail when use keyboard
      thumbnail.addEventListener('keydown', (e) => {
        if (KEYS.includes(e.key)) {
          slider.slick('slickGoTo', index);
        }
      });
    });

  loading = false;
};

/**
 * @type { import('./index.d').handleQuickViewModal }
 */
const handleQuickViewModal = (context) => {
  const modal = document.getElementById('boost-sd__modal-quickview');
  const closeButton = modal.querySelector('.boost-sd__modal-close-btn');
  const inputNumber = modal.querySelector('.boost-sd__input-number-input');
  const decreaseButton = modal.querySelector('.boost-sd__input-number-btn-wrapper:first-child');
  const increaseButton = modal.querySelector('.boost-sd__input-number-btn-wrapper:last-child');
  const errorQuantity = modal.querySelector('.boost-sd__quick-view-quantity-error');
  const atcButton = modal.querySelector('.boost-sd__btn-add-to-cart');
  const atcButtonText = modal.querySelector(
    '.boost-sd__btn-add-to-cart .boost-sd__button-text, .boost-sd__btn-add-to-cart span'
  );
  const btnBlock = modal.querySelector('.boost-sd__quick-view-btn');
  const orderBlock = modal.querySelector('.boost-sd__quick-view-preorder');
  const preorderButton = modal.querySelector('.boost-sd__button--preorder');
  const preorderButtonText = modal.querySelector(
    '.boost-sd__button--preorder .boost-sd__button-text'
  );
  const noteBlock = modal.querySelector('.boost-sd__preorder-note');
  const noteText = modal.querySelector('.boost-sd__preorder-note-text');
  const buyItNowButton = modal.querySelector('.boost-sd__btn-buy-now');
  const detailButton = modal.querySelector('.boost-sd__quick-view-details-link');
  const quickViewPrice = modal.querySelector('.boost-sd__quick-view-price');
  const quickViewPriceSpan = modal.querySelector('.boost-sd__quick-view-price span');
  const productSwatches = modal.querySelectorAll('.boost-sd__product-swatch');
  const saleLabels = modal.querySelectorAll('.boost-sd__product-label--sale');
  const soldOutLabels = modal.querySelectorAll('.boost-sd__product-label--soldout');
  const customLabels = modal.querySelectorAll('.boost-sd__product-label--customLabelByTag');
  const thumbItems = modal.querySelectorAll(
    '.boost-sd__thumbs-container-thumbs .boost-sd__thumbs-container-thumbs-item'
  );
  const thumbNextBtn = modal.querySelector('.boost-sd__thumbs-container-next-button button');
  const thumbPrevBtn = modal.querySelector('.boost-sd__thumbs-container-prev-button button');

  const {
    handle,
    splitProduct,
    variantId,
    variants,
    images,
    selectedVariantImageByFilterOption,
    options_with_values,
  } = safeParseJSON(modal.getAttribute('data-product')) || {};

  const [getImageIndex, setImageIndex] = context.useContextState('quick-view-image', 0);

  const selectedVariant = variants.find((variant) => variant.available) || variants[0];
  const [getVariant, setVariant] = context.useContextState('quick-view-swatch', {
    selectedVariant,
  });

  setVariant({ selectedVariant });

  // Replace the first image with the selected variant image by filter option
  if (selectedVariantImageByFilterOption && images?.length > 0) {
    images[0].src = selectedVariantImageByFilterOption;
  }

  const closeModal = () => {
    modal.style.display = 'none';
    modal.remove();
    document.body.classList.remove('boost-sd__modal-open-quickview');

    // Return focus to the button that opened the modal
    if (isKeyboardActivated() && window.buttonQuickviewFocus) {
      window.buttonQuickviewFocus.focus();
    }
  };

  closeButton?.addEventListener('click', closeModal);
  /**
   * Quick View - Support WCAG
   */
  focusFirstElement(modal);

  const handleModalKeydown = (e) => {
    trapFocus(e, modal);
  };

  modal.addEventListener('keydown', handleModalKeydown);
  /**
   * Quick View - Input Number Quantity
   */
  decreaseButton?.addEventListener('click', (e) => {
    e.preventDefault();
    inputNumber.value = Math.max(1, Number(inputNumber.value) - 1);

    errorQuantity.style.display = 'none';
  });

  increaseButton?.addEventListener('click', (e) => {
    e.preventDefault();
    inputNumber.value = Math.max(1, Number(inputNumber.value) + 1);

    errorQuantity.style.display = 'none';
  });

  function onlyNumbers(evt) {
    // Prevent non-numeric input
    const charCode = evt?.which ? evt.which : evt?.keyCode;
    if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
      evt.preventDefault();
    }
  }

  inputNumber.addEventListener('keypress', (e) => {
    onlyNumbers(e);
    errorQuantity.style.display = 'none';
  });

  modal?.addEventListener('click', (e) => {
    if (!e.target.closest('.boost-sd__modal-wrapper')) {
      closeModal();
    }
  });

  /**
   * Quick View - Swatches
   */
  const initialSelectedOption = (option) => {
    const label = option?.querySelector('.boost-sd__radio-label');
    if (!label) return;

    const selectedVariant = getVariant()?.selectedVariant;
    if (!selectedVariant || !selectedVariant.title) return;

    const selectedVariantTitle = selectedVariant.title;
    const optionTitle = getSwatchOptionTitle(label);

    const selectedVariantOptions = [];
    let remainingTitle = selectedVariantTitle;

    // Go through each option in the UI and match it against selectedVariantTitle
    modal.querySelectorAll('.boost-sd__radio-label').forEach((el) => {
      const text = getSwatchOptionTitle(el);
      if (remainingTitle.startsWith(text)) {
        selectedVariantOptions.push(text);
        remainingTitle = remainingTitle
          .replace(text, '')
          .trim()
          .replace(/^\/\s*/, ''); // Remove leading "/ "
      }
    });

    // Identify the current swatch's index
    const parentSwatch = option.closest('.boost-sd__product-swatch');
    const allSwatches = [...modal.querySelectorAll('.boost-sd__product-swatch')];
    const index = allSwatches.indexOf(parentSwatch);

    // Compare the extracted options correctly
    if (
      index !== -1 &&
      selectedVariantOptions[index]?.toLowerCase() === optionTitle.toLowerCase()
    ) {
      label.classList.add('boost-sd__radio-label--selected');
    }
  };

  const handleSelect = (option) => {
    const swatchOptions = option.parentNode.parentNode.querySelectorAll(
      '.boost-sd__product-swatch-option'
    );

    swatchOptions?.forEach((option) => {
      const label = option.querySelector('.boost-sd__radio-label');
      label?.classList.remove('boost-sd__radio-label--selected');
    });

    const label = option.querySelector('.boost-sd__radio-label');
    label?.classList.add('boost-sd__radio-label--selected');

    const selectedSwatchOptions = modal.querySelectorAll('.boost-sd__radio-label--selected');

    let title = '';
    selectedSwatchOptions?.forEach((option, i) => {
      title += getSwatchOptionTitle(option);

      if (i < selectedSwatchOptions.length - 1) {
        title += ' / ';
      }
    });

    const selectedVariant = variants?.find((variant) => {
      return variant.title === title;
    }) || {
      available: false,
      id: '',
      price: null,
    };

    setVariant({
      selectedVariant,
    });

    // Ensure sold-out class remains for all sold-out options
    updateSoldOutOption(option);
  };

  const toggleSoldOutClass = (label, isSoldOut) => {
    const soldOutClass = 'boost-sd__radio-label--sold-out';
    label.classList[isSoldOut ? 'add' : 'remove'](soldOutClass);
  };

  const updateRelatedLabels = (labels, variantOptions) => {
    labels.forEach((label) => {
      const labelText = getSwatchOptionTitle(label);
      if (variantOptions.includes(labelText)) {
        toggleSoldOutClass(label, false);
      }
    });
  };

  const handleVariantAvailability = (matchingVariant, label, labels) => {
    if (!matchingVariant) {
      toggleSoldOutClass(label, false);
      return;
    }

    if (!matchingVariant.available) {
      toggleSoldOutClass(label, true);
      return;
    }

    toggleSoldOutClass(label, false);
    const variantOptions = matchingVariant.title.split(' / ').map((opt) => opt.trim());
    updateRelatedLabels(labels, variantOptions);
  };

  const updateSoldOutOption = (option) => {
    const allVariantsSoldOut = variants.every((variant) => !variant.available);

    if (allVariantsSoldOut) {
      modal.querySelectorAll('.boost-sd__radio-label').forEach((label) => {
        label.classList.add('boost-sd__radio-label--sold-out');
      });

      return;
    }
    const selectedVariant = getVariant()?.selectedVariant;
    const selectedVariantTitle = selectedVariant?.title || '';

    const getOptions = (title) =>
      title.includes(' / ') ? title.split(' / ').slice(0, -1).join(' / ') : title;

    const selectedVariantOptions = getOptions(selectedVariantTitle);

    const matchingVariants = variants.filter(
      (variant) => getOptions(variant.title) === selectedVariantOptions
    );

    const labels = modal.querySelectorAll('.boost-sd__radio-label');

    labels.forEach((label) => {
      const labelText = getSwatchOptionTitle(label);

      const matchingVariant = matchingVariants.find((variant) =>
        variant.title.split(' / ').includes(labelText)
      );

      handleVariantAvailability(matchingVariant, label, labels);
    });

    // Handle the case where no matching variants exist
    if (matchingVariants.length === 0 && selectedVariant.available) {
      modal.querySelectorAll('.boost-sd__radio-label--selected').forEach((label) => {
        label.classList.remove('boost-sd__radio-label--sold-out');
      });
    } else if (!selectedVariant.available) {
      const selectedLabels = modal.querySelectorAll('.boost-sd__radio-label--selected');
      const noneSoldOut = Array.from(selectedLabels).every(
        (label) => !label.classList.contains('boost-sd__radio-label--sold-out')
      );

      if (noneSoldOut) {
        const label = option.querySelector('.boost-sd__radio-label');
        label?.classList.add('boost-sd__radio-label--sold-out');
      }
    }
  };

  const handleOptionKeyup = (option) => {
    return (e) => {
      if (KEYS.includes(e.key)) {
        handleSelect(option);
        updateSoldOutOption(option);
        updateSoldOutLabelsForSwatch(modal, variants);
      }
    };
  };

  // Trigger click when open modal
  const autoSelectFirstAvailableVariant = () => {
    const swatchOptions = modal.querySelectorAll('.boost-sd__product-swatch-option');
    const firstAvailableOption = Array.from(swatchOptions).find((option) => {
      const label = option.querySelector('.boost-sd__radio-label');

      return !label.classList.contains('boost-sd__radio-label--sold-out');
    });

    if (firstAvailableOption) {
      handleSelect(firstAvailableOption);
    }
  };

  productSwatches?.forEach((swatch) => {
    const swatchOptions = swatch.querySelectorAll('.boost-sd__product-swatch-option');
    swatchOptions?.forEach((option) => {
      initialSelectedOption(option);

      option.addEventListener('click', (e) => {
        handleSelect(option);
        updateSoldOutOption(option);
        updateSoldOutLabelsForSwatch(modal, variants);
      });

      option.addEventListener('keyup', handleOptionKeyup(option));
    });
  });

  autoSelectFirstAvailableVariant();

  const swatch_settings = context.app.filterSettings.swatch_settings;
  applySwatchSettings(modal, options_with_values, swatch_settings);
  updateSoldOutLabelsForSwatch(modal, variants);
  /* *
   * Quick View - Add to Cart/Buy it now
   */
  const { enableCart } = context?.templateSettings?.themeSettings?.cart?.enableCart || {};
  atcButton?.addEventListener('click', async () => {
    if (Number(inputNumber.value) <= 0) {
      errorQuantity.style.display = 'block';
      return;
    }

    // Add to cart logic here
    context.handler(addToCart, 'addToCart')(
      context,
      {
        productId: getVariant().selectedVariant.id,
        quantity: Number(inputNumber.value),
      },
      atcButton,
      enableCart,
      () => {
        // Shift focus to the first element in the cart modal
        setTimeout(() => {
          closeModal();
          const cartModal = document.querySelector('#boost-sd__modal-cart');
          if (cartModal) {
            focusFirstElement(cartModal);
          }
        }, 300);
      }
    );
  });

  preorderButton?.addEventListener('click', async () => {
    const selectedVariant = getVariant().selectedVariant;
    if (!selectedVariant) return;

    const addToCartData = handlePreorderAddToCart(selectedVariant);
    addToCartData.quantity = Number(inputNumber.value); // Ensure quantity is correctly assigned

    context.handler(addToCart, 'addToCart')(
      context,
      addToCartData,
      preorderButton,
      enableCart,
      () => {
        // Shift focus to the first element in the cart modal
        setTimeout(() => {
          closeModal();
          const cartModal = document.querySelector('#boost-sd__modal-cart');
          if (cartModal) {
            focusFirstElement(cartModal);
          }
        }, 300);
      }
    );
  });

  buyItNowButton?.addEventListener('click', async (e) => {
    e.stopImmediatePropagation();
    e.preventDefault();

    const quantity = Number(inputNumber.value || 1);
    const variantId = getVariant().selectedVariant.id;
    if (quantity <= 0) return;

    const cart = await CartAPI.getAll();
    localStorage.setItem('backup_cart_items', JSON.stringify(cart.items));
    localStorage.setItem('buy_it_now_in_progress', 'true');

    await CartAPI.clearCart();
    await CartAPI.add(variantId, quantity);

    window.location.href = '/checkout';
  });

  /**
   * Quick View - Prev/Next
   */
  thumbPrevBtn?.addEventListener('click', () => {
    const index = getImageIndex();
    const currentImgIndex = index - 1;

    handleChangeSliderImagesPortion(
      context,
      currentImgIndex,
      images[currentImgIndex]?.src,
      images,
      thumbItems,
      modal,
      setImageIndex
    );
  });

  thumbNextBtn?.addEventListener('click', () => {
    const index = getImageIndex();
    const currentImgIndex = index + 1;

    handleChangeSliderImagesPortion(
      context,
      currentImgIndex,
      images[currentImgIndex]?.src,
      images,
      thumbItems,
      modal,
      setImageIndex
    );
  });

  thumbItems?.forEach((thumbnail, index) => {
    thumbnail.addEventListener('click', () => {
      handleChangeSliderImagesPortion(
        context,
        index,
        images?.[index]?.src,
        images,
        thumbItems,
        modal,
        setImageIndex
      );
    });
  });

  handleChangeSliderImagesPortion(
    context,
    0,
    images[0]?.src,
    images,
    thumbItems,
    modal,
    setImageIndex
  );

  function showPreorderBlock(preorderVariant) {
    document?.querySelectorAll('.boost-sd__preorder').forEach(function (block) {
      block.style.display = 'none';
    });
    const matchingPreorderBlock = document.querySelector(
      `.boost-sd__preorder[data-variant-id="${preorderVariant}"]`
    );
    if (matchingPreorderBlock) {
      matchingPreorderBlock.style.display = 'block';
    }
  }

  context.render(() => {
    const { selectedVariant } = getVariant();

    /**
     *  Quick View - Price
     */
    const { showCurrencyCodes } =
      context?.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};

    const price = formatCurrency({
      context,
      value: selectedVariant?.price,
      showCurrencyCodes,
    });
    const isSale =
      (Number(selectedVariant?.compare_at_price) || 0) > (Number(selectedVariant?.price) || 0);

    const { addCollectionToProductUrl, current_tags = [] } = context?.app?.generalSettings || {};

    const detailUrl = buildProductDetailUrlWithVariant(
      context,
      {
        variants,
        handle,
        split_product: splitProduct,
        variantId,
      },
      addCollectionToProductUrl,
      current_tags,
      selectedVariant.id
    );

    if (price) {
      if (quickViewPrice) {
        quickViewPrice.style.display = 'block';
      }
      if (quickViewPriceSpan) {
        quickViewPriceSpan.innerHTML = price;
      }
    }

    if (!selectedVariant?.price) {
      if (quickViewPrice) {
        quickViewPrice.style.display = 'none';
      }
    }

    const compareAtPriceSpan = document.createElement('span');
    compareAtPriceSpan.className =
      'boost-sd__format-currency boost-sd__format-currency--price-compare';

    // Instead of checking childElementCount, specifically look for the compare price element
    const existingCompareAtPrice = quickViewPrice.querySelector(
      '.boost-sd__format-currency--price-compare'
    );
    if (existingCompareAtPrice) {
      existingCompareAtPrice.remove();
    }

    if (isSale) {
      const compareAtPrice = formatCurrency({
        context,
        value: selectedVariant?.compare_at_price,
        showCurrencyCodes,
      });

      compareAtPriceSpan.innerHTML = compareAtPrice;
      // Insert after the main price element
      quickViewPriceSpan.insertAdjacentElement('afterend', compareAtPriceSpan);
    }

    /**
     * Quick View - Add to Cart/Buy it now
     */
    if (!atcButton || !selectedVariant) return;

    const isSoldOutVariantSelected = !selectedVariant.available;
    const elements = {
      btnBlock,
      orderBlock,
      preorderButtonText,
      noteBlock,
      noteText,
      showPreorderBlock,
    };

    handlePreOrderUI(selectedVariant, elements);

    if (isSoldOutVariantSelected) {
      if (atcButton) {
        btnBlock.querySelector('.boost-sd__btn-add-to-cart').setAttribute('disabled', true);
        if (atcButtonText) {
          atcButtonText.textContent =
            context.app?.translation?.productItem?.soldoutLabel || 'Sold out';
        }
      }

      if (buyItNowButton) {
        buyItNowButton.style.display = 'none';
      }
    } else {
      if (atcButton) {
        btnBlock.querySelector('.boost-sd__btn-add-to-cart').removeAttribute('disabled', true);
        if (atcButtonText) {
          atcButtonText.textContent =
            context.app?.translation?.productItem?.atcAvailableLabel || 'Add to cart';
        }
      }

      if (buyItNowButton) {
        buyItNowButton.style.display = 'flex';
      }
    }

    // For analytics
    btnBlock
      .querySelector('.boost-sd__btn-add-to-cart')
      .setAttribute('data-variant-id', selectedVariant.id);

    buyItNowButton?.setAttribute('data-variant-id', selectedVariant.id);

    detailButton.setAttribute('href', detailUrl);

    /**
     * Quick View - Slider
     * Update slider image based on selected variant
     */
    if (selectedVariant?.image) {
      const src = selectedVariant.image;
      const index = images?.findIndex((image) => image.src === src);

      handleChangeSliderImagesPortion(
        context,
        index,
        src,
        images,
        thumbItems,
        modal,
        setImageIndex
      );
    }

    /**
     * Quick View - Labels
     * Update sale and sold out labels based on selected variant
     */
    if (isSoldOutVariantSelected) {
      soldOutLabels.forEach((soldOutLabel) => {
        soldOutLabel.style.display = 'flex';
      });
      saleLabels?.forEach((saleLabel) => {
        saleLabel.style.display = 'none';
      });
      customLabels?.forEach((customLabel) => {
        customLabel.style.display = 'none';
      });
    } else if (isSale && !isSoldOutVariantSelected) {
      soldOutLabels.forEach((soldOutLabel) => {
        soldOutLabel.style.display = 'none';
      });
      saleLabels?.forEach((saleLabel) => {
        saleLabel.style.display = 'flex';
      });
      customLabels?.forEach((customLabel) => {
        customLabel.style.display = 'flex';
      });
    } else {
      soldOutLabels.forEach((soldOutLabel) => {
        soldOutLabel.style.display = 'none';
      });
      saleLabels?.forEach((saleLabel) => {
        saleLabel.style.display = 'none';
      });
      customLabels?.forEach((customLabel) => {
        customLabel.style.display = 'flex';
      });
    }
  }, ['quick-view-swatch']);

  context.render(() => {
    const currentIndex = getImageIndex();

    if (currentIndex === 0) {
      thumbPrevBtn?.setAttribute('disabled', true);
      thumbNextBtn?.removeAttribute('disabled');
    } else if (currentIndex === images.length - 1) {
      thumbNextBtn?.setAttribute('disabled', true);
      thumbPrevBtn?.removeAttribute('disabled');
    } else {
      thumbPrevBtn?.removeAttribute('disabled');
      thumbNextBtn?.removeAttribute('disabled');
    }
  }, ['quick-view-image']);
};

/**
 * @type { import('./index.d').selectThumbnail }
 */
const selectThumbnail = (currentSlide) => {
  // remove selected class from all thumbnails
  const SELECTED_THUMBNAIL = 'boost-sd__thumbs-container-thumbs-item--selected';
  const currentSelectedThumbnail = document.querySelector(`.${SELECTED_THUMBNAIL}`);
  currentSelectedThumbnail?.classList.remove(SELECTED_THUMBNAIL);

  const selectedThumbnail = document.querySelector(
    `.boost-sd__thumbs-container-thumbs-item:nth-child(${currentSlide + 1})`
  );

  selectedThumbnail?.classList.add(SELECTED_THUMBNAIL);
};

/**
 * @type { import('./index.d').handleChangeSliderImagesPortion }
 */
const handleChangeSliderImagesPortion = (
  context,
  index,
  src,
  images,
  thumbItems,
  modal,
  setImageIndex
) => {
  if (!images || !thumbItems || !modal) return;

  const lastPortion = Math.floor((images?.length || 0) / 4) + 1;
  const portion = Math.floor(index / 4) + 1;
  const startHiding = (portion - 1) * 4;
  const endHiding = startHiding + 3;

  thumbItems.forEach((thumbnail, i) => {
    const img = thumbnail.querySelector('img');

    if (i < startHiding || i > endHiding) {
      thumbnail.style.display = 'none';
    } else {
      thumbnail.style.display = 'block';
    }

    if (img.src === src) {
      setImageIndex(index);
      sliderRef.slick('slickGoTo', i);
    }
  });

  /**
   * Borrow from previous portion
   * For example: if the last portion (portion 4) has 2 images
   * then the last 2 images of the previous portion (portion 3) + 2 images of the last  portion (portion 4) should be displayed
   */
  if (portion === lastPortion && lastPortion > 1) {
    const lastPortionLength = images.length % 4;
    const borrowFromPreviousPortion = 4 - lastPortionLength;

    for (let i = 0; i < borrowFromPreviousPortion; i++) {
      thumbItems[images.length - lastPortionLength - 1 - i].style.display = 'block';
    }
  }
};

/**
 * @type { import('./index.d').updateSoldOutLabelsForSwatch }
 */
function updateSoldOutLabelsForSwatch(modal, variants) {
  const swatchGroup = modal.querySelector('.boost-sd__product-swatch');
  if (!swatchGroup) return;

  swatchGroup.querySelectorAll('.boost-sd__product-swatch-option').forEach((option) => {
    const label = option.querySelector('.boost-sd__radio-label');
    if (!label) return;
    const optionValue = getSwatchOptionTitle(label);
    const variant = variants.find(
      (v) =>
        (v.title && v.title.trim() === optionValue) ||
        (v.merged_options && v.merged_options.some((mo) => mo.endsWith(optionValue)))
    );
    if (!variant || !variant.available) {
      label.classList.add('boost-sd__radio-label--sold-out');
    } else {
      label.classList.remove('boost-sd__radio-label--sold-out');
    }
  });
}

function getSwatchOptionTitle(label) {
  return (
    (label?.getAttribute('data-swatch-value') || '').trim() || (label?.textContent || '').trim()
  );
}
